import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";

import {
  Dropdown,
  DropdownMenu,
  IconButton,
  Dialog,
  MediaCard,
} from "@toddle-edu/ds-web";
import {
  PlaceholderFileOutlined,
  DotsVerticalOutlined,
  TickLargeOutlined,
  EyeOutlined,
  PencilLineOutlined,
  PencilOutlined,
  CopyOutlined,
  DeleteOutlined,
} from "@toddle-edu/ds-icons";

import { useI18n } from "Hooks";

import TemplateRenameModal from "./TemplateRenameModal";

import classNames from "classnames";
import classes from "./WorkbookTemplateCard.scss";

const WorkbookTemplateCard = ({
  id,
  attachment,
  url,
  thumbUrl,
  label,
  creator,
  numOfPages,
  isTemplateCreator,
  onUseTemplateClick,
  onPreviewTemplateClick,
  onEditTemplateClick,
  onDeleteTemplateClick,
  onRenameTemplateClick,
  onDuplicateTemplateClick,
  isAdmin,
}) => {
  const { t } = useI18n();

  const [isDropdownMenu, setIsDropdownMenu] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  const menuItems = useMemo(
    () => [
      {
        key: "use_school_template",
        label: t("classRoom:use_template"),
        icon: <TickLargeOutlined size="xx-small" variant="subtle" />,
        isDestructive: false,
      },
      {
        key: "preview",
        label: t("common:preview"),
        icon: <EyeOutlined size="xx-small" variant="subtle" />,
        isDestructive: false,
      },
    ],
    [t]
  );
  const ownerMenuItems = useMemo(
    () => [
      {
        key: "use_school_template",
        label: t("classRoom:use_template"),
        icon: <TickLargeOutlined size="xx-small" variant="subtle" />,
        isDestructive: false,
      },
      {
        key: "preview",
        label: t("common:preview"),
        icon: <EyeOutlined size="xx-small" variant="subtle" />,
        isDestructive: false,
      },
      {
        key: "edit_original_template",
        label: t("common:edit_original_template"),
        icon: <PencilLineOutlined size="xx-small" variant="subtle" />,
        isDestructive: false,
      },
      {
        key: "delete",
        label: t("common:delete"),
        icon: <DeleteOutlined size="xx-small" variant="critical" />,
        isDestructive: true,
      },
    ],
    [t]
  );
  const adminMenuItems = useMemo(
    () => [
      {
        key: "edit_original_template",
        label: t("common:edit"),
        icon: <PencilOutlined size="xx-small" variant="subtle" />,
        isDestructive: false,
      },
      {
        key: "rename_template",
        label: t("common:rename"),
        icon: <PencilLineOutlined size="xx-small" variant="subtle" />,
        isDestructive: false,
      },
      {
        key: "duplicate_template",
        label: t("common:duplicate"),
        icon: <CopyOutlined size="xx-small" variant="subtle" />,
        isDestructive: false,
      },
      {
        key: "delete",
        label: t("common:delete"),
        icon: <DeleteOutlined size="xx-small" variant="critical" />,
        isDestructive: true,
      },
    ],
    [t]
  );

  const dropDownOption = useMemo(
    () =>
      isAdmin ? adminMenuItems : isTemplateCreator ? ownerMenuItems : menuItems,
    [isAdmin, isTemplateCreator, adminMenuItems, ownerMenuItems, menuItems]
  );

  const subTitleStyle = classNames("text-body-3", classes.subtitleContainer);

  const onActionClick = e => {
    switch (e?.key) {
      case "use_school_template":
        onUseTemplateClick({ id, label, isTemplateCreator, attachment });
        break;
      case "preview":
        onPreviewTemplateClick({
          id,
          label,
          isTemplateCreator,
          attachment,
        });
        break;
      case "edit_original_template":
        onEditTemplateClick({ id, attachment, label });
        break;
      case "delete":
        setIsDeleteDialogOpen(true);
        break;
      case "rename_template":
        setIsRenameModalOpen(true);
        break;
      case "duplicate_template":
        onDuplicateTemplateClick({
          attachment,
          label,
        });
        break;
      default:
        break;
    }
    setIsDropdownMenu(false);
  };

  const handleTemplateClicked = () => {
    onUseTemplateClick({ id, label, isTemplateCreator, attachment });
  };

  const handleRenameTemplate = label => {
    onRenameTemplateClick({
      id,
      attachment,
      label,
    });
    setIsRenameModalOpen(false);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteTemplate = () => {
    onDeleteTemplateClick({ id, label });
    setIsDeleteDialogOpen(false);
  };

  const handleEventPropogation = e => {
    e.stopPropagation();
  };

  const closeRenameModal = () => {
    setIsRenameModalOpen(false);
  };

  const renderDropDownOptions = () => (
    <div onClick={handleEventPropogation}>
      <Dropdown
        dsVersion="2.0"
        overlay={
          <DropdownMenu
            dsVersion="2.0"
            options={dropDownOption}
            onClick={onActionClick}
          />
        }
        onVisibleChange={setIsDropdownMenu}
      >
        <IconButton
          dsVersion={"2.0"}
          type={"plain"}
          variant={"neutral"}
          size={"small"}
          icon={<DotsVerticalOutlined variant="subtle" />}
        />
      </Dropdown>
    </div>
  );

  const renderRenameModal = () =>
    isRenameModalOpen && (
      <TemplateRenameModal
        name={label}
        closeModal={closeRenameModal}
        onUpdateName={handleRenameTemplate}
      />
    );

  const renderDeleteDialog = () =>
    isDeleteDialogOpen && (
      <Dialog
        dsVersion="2.0"
        variant="delete-dialog"
        title={t("workbook:delete_workbook_template")}
        message={t("workbook:sure_want_to_delete_template_with_label", {
          label: label,
        })}
        buttonOneText={t("common:cancel")}
        buttonTwoText={t("common:delete")}
        onClose={handleCloseDeleteDialog}
        onButtonOneClick={handleCloseDeleteDialog}
        onButtonTwoClick={handleDeleteTemplate}
      />
    );

  const renderSubtitle = () => (
    <div className={subTitleStyle}>
      <div className={classes.dot}></div>
      <div className={classes.pageContainer}>
        <PlaceholderFileOutlined size={"xxxx-small"} variant="subtle" />
        <div>{numOfPages}</div>
      </div>
    </div>
  );

  return (
    <>
      <MediaCard
        dsVersion="2.0"
        cardType={"medium"}
        subtitleSuffix={renderSubtitle()}
        hideThumbnail
        name={label}
        subtext={creator}
        url={url}
        thumbUrl={thumbUrl}
        type={"workbook"}
        showMediaPreview={true}
        showActionsOnHover={true}
        actionsDropdownVisibility={isDropdownMenu}
        onClick={handleTemplateClicked}
        options={renderDropDownOptions()}
        ratio={"16:9"}
      />
      {renderDeleteDialog()}
      {renderRenameModal()}
    </>
  );
};

WorkbookTemplateCard.prototype = {
  id: PropTypes.string,
  attachment: PropTypes.object,
  url: PropTypes.string,
  thumbUrl: PropTypes.string,
  label: PropTypes.string,
  creator: PropTypes.string,
  numOfPages: PropTypes.number,
  isAdmin: PropTypes.bool,
  isTemplateCreator: PropTypes.bool,
  onUseTemplateClick: PropTypes.func,
  onPreviewTemplateClick: PropTypes.func,
  onEditTemplateClick: PropTypes.func,
  onDeleteTemplateClick: PropTypes.func,
  onRenameTemplateClick: PropTypes.func,
  onDuplicateTemplateClick: PropTypes.func,
};

export default WorkbookTemplateCard;
